import React, { useEffect, useMemo } from 'react'
import { useQuery, gql } from '@apollo/client'
import LinearProgress from '@material-ui/core/LinearProgress'
import Error from 'components/error'
import MenuItem from '@material-ui/core/MenuItem'
import FormHelperText from '@material-ui/core/FormHelperText'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import { Box } from '@material-ui/core'
import { useHistory, useParams } from 'react-router-dom'
import useStyles from './styles'

export const COACHING_CENTER_AFFILIATION_QUERY = gql`
  query GetCoachingCenterAffiliations {
    currentUser {
      attributes {
        ... on Coach {
          employments {
            role
            academy {
              id
              name
              slug
            }
          }
        }
      }
    }
  }
`

export const ownedAcademiesFromAffiliations = data =>
  (data?.currentUser?.attributes?.employments || [])
    .filter(employment => employment.role === 'OWNER')
    .map(employment => employment.academy)
    .filter(Boolean)

const OwnedCoachingCentersContext = React.createContext(null)

export const useOwnedCoachingCentersQuery = () => {
  const { data, loading, error } = useQuery(COACHING_CENTER_AFFILIATION_QUERY)
  const ownedCoachingCenters = useMemo(
    () => ownedAcademiesFromAffiliations(data),
    [data],
  )
  return { ownedCoachingCenters, loading, error }
}

export const OwnedCoachingCentersProvider = ({ children }) => {
  const value = useOwnedCoachingCentersQuery()
  return (
    <OwnedCoachingCentersContext.Provider value={value}>
      {children}
    </OwnedCoachingCentersContext.Provider>
  )
}

export const useOwnedCoachingCenters = () => {
  const context = React.useContext(OwnedCoachingCentersContext)
  const fallbackQuery = useQuery(COACHING_CENTER_AFFILIATION_QUERY, {
    skip: Boolean(context),
  })
  const fallbackAcademies = useMemo(
    () => ownedAcademiesFromAffiliations(fallbackQuery.data),
    [fallbackQuery.data],
  )
  if (context) return context
  return {
    ownedCoachingCenters: fallbackAcademies,
    loading: fallbackQuery.loading,
    error: fallbackQuery.error,
  }
}

const CoachingCenterListDropDown = ({ showHelperText = true }) => {
  const { ownedCoachingCenters, loading, error } = useOwnedCoachingCenters()
  const history = useHistory()
  const classes = useStyles()
  const { slug } = useParams()

  const selectedSlug =
    ownedCoachingCenters.find(academy => academy.slug === slug)?.slug ||
    ownedCoachingCenters[0]?.slug ||
    ''

  useEffect(() => {
    if (!ownedCoachingCenters.length) return
    if (slug === selectedSlug) return
    history.replace(`/account/coaching-center/${selectedSlug}`)
  }, [history, ownedCoachingCenters.length, selectedSlug, slug])

  if (loading) return <LinearProgress />
  if (error) return <Error error={error} />
  if (!ownedCoachingCenters.length) return null

  const selectDropdown = ccSlug => {
    history.push(`/account/coaching-center/${ccSlug}`)
  }

  return (
    <Box className={classes.dropdown}>
      <FormControl variant="outlined" className={classes.formControl}>
        <Select
          id="coaching-center-select"
          value={selectedSlug}
          onChange={event => selectDropdown(event.target.value)}
          className={classes.select}
          displayEmpty
          inputProps={{ 'aria-label': 'Academy' }}
        >
          {ownedCoachingCenters.map(academy => (
            <MenuItem key={academy.id} value={academy.slug}>
              {academy.name}
            </MenuItem>
          ))}
        </Select>
        {showHelperText && (
          <FormHelperText>Select an academy to see all students</FormHelperText>
        )}
      </FormControl>
    </Box>
  )
}

export default CoachingCenterListDropDown
